import { z } from 'zod';
import {
  PAGINATION_PAGE_SIZE_LARGE,
  PAGINATION_SORT_DIRECTION,
} from '~/constants';

import { toSnakeCase } from '@vestigas/utils';

import {
  SITE_SIGNATURE_RATES_BREAKDOWN_SORT_BY,
  SITE_SIGNATURE_RATES_SIGNATURE_FILTER,
} from './constants';

/**
 * Zod schema for validating top sites breakdown query parameters.
 */
export const zodTopSitesBreakdownQueryParamsSchema = z.object({
  dateRange: z.object({
    dateFrom: z.iso.date(),
    dateTo: z.iso.date(),
  }),
  limit: z.number().int().positive().prefault(PAGINATION_PAGE_SIZE_LARGE),
  offset: z.number().int().nonnegative().prefault(0),
  signatureFilter: z
    .preprocess(
      (value) => (typeof value === 'string' ? toSnakeCase(value) : value),
      z.enum(SITE_SIGNATURE_RATES_SIGNATURE_FILTER),
    )
    .prefault('all'),
  sortBy: z
    .preprocess(
      (value) => (typeof value === 'string' ? toSnakeCase(value) : value),
      z.enum(SITE_SIGNATURE_RATES_BREAKDOWN_SORT_BY),
    )
    .optional()
    .prefault('signature_rate'),
  sortOrder: z
    .preprocess(
      (value) => String(value).toUpperCase(),
      z.enum(PAGINATION_SORT_DIRECTION),
    )
    .prefault('DESC'),
});

/**
 * Zod schema for validating a single site breakdown item.
 */
const zodSiteBreakdownItemSchema = z.object({
  signatureRate: z.number().nonnegative(),
  signedDeliveryNotesCount: z.number().int().nonnegative(),
  siteId: z.uuid(),
  siteName: z.string(),
  totalDeliveryNotesCount: z.number().int().nonnegative(),
  unsignedDeliveryNotesCount: z.number().int().nonnegative(),
});

/**
 * Zod schema for validating the top sites breakdown API response.
 */
export const zodTopSitesBreakdownResponseSchema = z.object({
  data: z.array(zodSiteBreakdownItemSchema),
  hasNextPage: z.boolean(),
  totalCount: z.number().int().nonnegative(),
});

/**
 * Zod schema for validating the top sites breakdown API endpoint path segments.
 */
export const zodTopSitesBreakdownEndpointSchema = z.tuple([
  z.literal('site'),
  z.literal('statistics'),
  z.literal('top_sites'),
]);
